import React from 'react';

export const STEP_DEFS = [
  { id: 'profile', label: 'Complete your profile' },
  { id: 'resume', label: 'Upload your resume' },
  { id: 'skills', label: 'Add your skills' },
  { id: 'prescreening', label: 'Complete prescreening' },
  { id: 'assessment', label: 'Take your first assessment' },
  { id: 'gaps', label: 'Review your skill gaps' },
  { id: 'learning', label: 'Explore recommended learning' },
  { id: 'jobs', label: 'Explore career opportunities' },
];

export const STEP_HINTS = {
  profile: 'Add the details employers see on your profile.',
  resume: 'Upload a resume so we can extract your experience.',
  skills: 'List your skills to match assessments, courses, and jobs.',
  prescreening: 'Answer a short background questionnaire.',
  assessment: 'Take an assessment to see where you stand.',
  gaps: 'Open your report to see strengths and gaps.',
  learning: 'Explore a course for the skills you still need.',
  jobs: 'Browse roles that match your profile.',
  'continue-assessment': 'Pick up from the last question you answered.',
};

export const VALUE_COPY = {
  profile: {
    title: 'Complete your profile',
    body: 'Add the details employers see on your profile.',
    cta: 'Complete Profile',
  },
  resume: {
    title: 'Upload your resume',
    body: 'Upload a resume so we can extract your experience.',
    cta: 'Upload Resume',
  },
  skills: {
    title: 'Add your skills',
    body: 'List your skills to match assessments, courses, and jobs.',
    cta: 'Add Skills',
  },
  prescreening: {
    title: 'Complete prescreening',
    body: 'Answer a short background questionnaire.',
    cta: 'Start Prescreening',
  },
  assessment: {
    title: 'Take your first assessment',
    body: 'Take an assessment to see where you stand.',
    cta: 'Start Assessment',
  },
  gaps: {
    title: 'Review your skill gaps',
    body: 'Open your report to see strengths and gaps.',
    cta: 'Review Reports',
  },
  learning: {
    title: 'Explore recommended learning',
    body: 'Explore a course for the skills you still need.',
    cta: 'Explore Learning',
  },
  jobs: {
    title: 'Explore career opportunities',
    body: 'Browse roles that match your profile.',
    cta: 'Explore Jobs',
  },
  'continue-assessment': {
    title: 'Continue your assessment',
    body: 'Pick up from the last question you answered.',
    cta: 'Continue Assessment',
  },
};

export function buildJourneySteps({
  profile,
  hasResume,
  prescreeningCompleted,
  hasCompletedAssessment,
  hasReport,
  hasExploredLearning,
  hasExploredJobs,
}) {
  const completion = profile?.profileCompletionPercentage || 0;
  const skills = Array.isArray(profile?.skills) ? profile.skills : [];
  return [
    { ...STEP_DEFS[0], complete: completion >= 100, ctaView: 'profile' },
    { ...STEP_DEFS[1], complete: Boolean(hasResume), ctaView: 'profile' },
    { ...STEP_DEFS[2], complete: skills.length > 0, ctaView: 'profile' },
    {
      ...STEP_DEFS[3],
      complete: Boolean(prescreeningCompleted),
      ctaView: 'prescreening',
    },
    {
      ...STEP_DEFS[4],
      complete: Boolean(hasCompletedAssessment),
      ctaView: 'assessments',
    },
    { ...STEP_DEFS[5], complete: Boolean(hasReport), ctaView: 'reports' },
    { ...STEP_DEFS[6], complete: Boolean(hasExploredLearning), ctaView: 'courses' },
    { ...STEP_DEFS[7], complete: Boolean(hasExploredJobs), ctaView: 'jobs' },
  ];
}

export function selectNextBestAction(steps, inProgressAssessment) {
  if (inProgressAssessment) {
    return {
      id: 'continue-assessment',
      label: inProgressAssessment.title || 'Continue your assessment',
      complete: false,
      ctaView: 'continue-assessment',
    };
  }
  return steps.find((step) => !step.complete) || null;
}

export function journeyCompletionPercent(steps) {
  if (!steps.length) return 0;
  const completedCount = steps.filter((step) => step.complete).length;
  return Math.round((completedCount / steps.length) * 100);
}

const OnboardingJourney = ({
  steps,
  inProgressAssessment,
  onStepClick,
}) => {
  const completedCount = steps.filter((step) => step.complete).length;
  const nextStep = selectNextBestAction(steps, inProgressAssessment);
  const copy = (nextStep && VALUE_COPY[nextStep.id]) || {
    title: nextStep ? nextStep.label : 'You are all set',
    body: nextStep
      ? STEP_HINTS[nextStep.id] || 'This is the next step on your Liaizn journey.'
      : 'You have finished the basics. Keep going with assessments, courses, and jobs.',
    cta: nextStep ? 'Continue' : 'Explore Jobs',
  };
  const nextTitle = nextStep?.label || copy.title;
  const nextHint = STEP_HINTS[nextStep?.id] || copy.body;

  const percent = journeyCompletionPercent(steps);

  return (
    <section className="ui-card relative isolate flex h-full flex-col p-4" aria-labelledby="liaizn-journey-heading">
      <div>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
          <h2 id="liaizn-journey-heading" className="text-lg font-semibold text-secondary-900">
            Your Liaizn Journey
          </h2>
          <p className="text-sm text-secondary-500">
            {completedCount} of {steps.length} steps complete
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <p className="w-10 shrink-0 text-sm font-semibold tabular-nums text-secondary-700">
            {percent}%
          </p>
          <div
            className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-secondary-100"
            role="progressbar"
            aria-label="Onboarding steps"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-valuetext={`${completedCount} of ${steps.length} steps complete`}
          >
            <div
              className="h-full rounded-full bg-primary-600"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 rounded-md border border-primary-100 bg-primary-50 px-2.5 py-1.5 sm:flex-row sm:items-center sm:justify-between">
        <p className="min-w-0 text-xs text-secondary-600 sm:truncate">
          <span className="font-semibold uppercase tracking-wide text-primary-700">
            {nextStep ? 'Up next' : 'All caught up'}
          </span>
          <span className="mx-1.5 font-semibold text-secondary-900">{nextTitle}</span>
          <span>
            {inProgressAssessment
              ? `${inProgressAssessment.answered_count || 0} of ${inProgressAssessment.total_questions || 0} questions answered`
              : nextHint}
          </span>
        </p>
        <button
          type="button"
          onClick={() => onStepClick(nextStep || { ctaView: 'jobs' }, { source: 'nba' })}
          className="btn-primary w-full shrink-0 py-1 px-2.5 text-xs sm:w-auto"
        >
          {inProgressAssessment ? 'Continue' : copy.cta}
        </button>
      </div>

      <ol className="mt-3 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
        {steps.map((step, index) => {
          const isNext = Boolean(nextStep) && nextStep.id === step.id && !inProgressAssessment;
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => onStepClick(step, { source: 'checklist' })}
                aria-label={`${step.complete ? 'Completed' : 'Go to'}: ${step.label}`}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-sm transition-colors ${
                  isNext ? 'bg-primary-50 text-primary-900' : 'hover:bg-secondary-50'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                    step.complete
                      ? 'bg-success-600 text-white'
                      : isNext
                        ? 'bg-primary-600 text-white'
                        : 'bg-secondary-100 text-secondary-500'
                  }`}
                  aria-hidden="true"
                >
                  {step.complete ? '✓' : index + 1}
                </span>
                <span className={`min-w-0 truncate ${step.complete ? 'text-secondary-500 line-through' : 'text-secondary-800'}`}>
                  {step.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default OnboardingJourney;
